/**
 * y축 스케일 보정 — **한 지표가 너무 커서 나머지가 x축에 붙는** 문제를 푼다.
 *
 * 한 그래프에 CPU 사용률(0~100)과 Executions/sec(수천)을 같이 올리면 작은 쪽이 바닥에
 * 붙어 한 줄로 뭉개진다. 값이 보이지 않으니 있으나 마나 한 계열이 된다.
 *
 * 두 가지를 함께 쓴다(사용자 결정 2026-09-21).
 *   1) **중간 생략(끊긴 축)** — 위쪽 큰 값과 아래쪽 작은 값 사이의 **빈 구간**을 접는다.
 *      데이터가 없는 구간만 접으므로 잃는 정보가 없다. 자동으로 걸린다.
 *   2) **최대값 드래그** — 사용자가 직접 위쪽을 잘라 낸다. 손을 대면 1)은 끈다
 *      (두 왜곡이 겹치면 축을 아무도 읽을 수 없다).
 *
 * **끊긴 축은 기울기를 왜곡한다.** 그래서 걸 때는 까다롭게 걸고(아래 임계값), 축에 반드시
 * 표시를 남긴다 — 표시 없는 끊긴 축은 거짓말이다.
 */

/** 표시 좌표계는 0~100 으로 고정한다 — 실제 픽셀은 recharts 가 맡는다 */
// 눈금 자릿수 맞추기(2026-10-16)는 recharts 가 눈금을 고르는 바로 그 함수로 눈금을 다시 구해 본다 — 비슷한 함수를 따로 두면 눈금과 자릿수가 어긋난다.
// recharts 3 의 내부 경로라 판을 올리면 확인할 것(chartYScale.test 가 실제 눈금과 대조한다)
import { getNiceTickValues, getTickValuesFixedDomain } from 'recharts/lib/util/scale/getNiceTickValues';

const DISPLAY_MAX = 100;
/** 아래 구간 / 끊긴 자리 / 위 구간에 나눠 줄 몫 */
const LOWER_SPAN = 62;
const BREAK_SPAN = 6;
const UPPER_SPAN = DISPLAY_MAX - LOWER_SPAN - BREAK_SPAN;

/** 빈 구간이 이만큼은 돼야 접는다 — 조금만 떨어져 있어도 접으면 축이 누더기가 된다.
 *  0.45 로 뒀더니 실제로 접어야 할 모양(작은 무리 ~78, 큰 값 200~280)이 걸러졌다. */
const MIN_GAP_RATIO = 0.35;
/** 아래 무리가 전체의 이보다 크면 접을 이유가 없다(이미 둘 다 보인다) */
const MAX_LOW_RATIO = 0.35;

function finiteNumbers(values) {
    return (values || [])
        .map(Number)
        .filter(v => Number.isFinite(v));
}

/**
 * 접을 만한 **빈 구간**을 찾는다.
 *
 * @param {number[]} values 그 축에 올라가는 모든 값
 * @returns {{lo: number, hi: number, max: number}|null}
 */
function detectAxisBreak(values) {
    const nums = finiteNumbers(values);
    if (nums.length < 3) return null;
    // 음수가 섞이면 접지 않는다 — 0 기준선이 어디인지 흐려져 득보다 실이 크다
    if (nums.some(v => v < 0)) return null;

    const max = Math.max(...nums);
    if (!(max > 0)) return null;

    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    let best = null;
    for (let i = 0; i < sorted.length - 1; i++) {
        const gap = sorted[i + 1] - sorted[i];
        if (!best || gap > best.gap) best = { gap, lo: sorted[i], hi: sorted[i + 1] };
    }
    if (!best) return null;
    if (best.gap < max * MIN_GAP_RATIO) return null;      // 빈 구간이 얕다
    if (best.lo > max * MAX_LOW_RATIO) return null;       // 아래 무리가 이미 충분히 높다
    if (best.lo <= 0) return null;                        // 아래에 볼 것이 없다(전부 0)
    return { lo: best.lo, hi: best.hi, max };
}

/** 1·2·5 계열의 보기 좋은 간격 */
function niceStep(range, count) {
    if (!(range > 0) || count < 1) return 1;
    const rough = range / count;
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / mag;
    const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
    return step * mag;
}

function ticksUpTo(from, to, count) {
    const step = niceStep(to - from, count);
    const out = [];
    for (let v = Math.ceil(from / step) * step; v <= to + step * 1e-9; v += step) {
        out.push(Math.round(v * 1e6) / 1e6);
    }
    return out;
}

/**
 * 끊긴 축의 변환기.
 *
 * `toDisplay` 로 값을 0~100 좌표로 옮겨 그리고, 축 눈금은 `fromDisplay` 로 되돌려 **원래
 * 값**을 적는다. 그래야 사람이 읽는 숫자는 끝까지 진짜 값이다.
 *
 * @param {{lo: number, hi: number, max: number}} brk
 */
function makeBreakScale(brk) {
    const { lo, hi, max } = brk;
    const upperRange = max - hi;

    const toDisplay = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        if (n <= lo) return (n / lo) * LOWER_SPAN;
        if (n >= hi) {
            return upperRange > 0
                ? LOWER_SPAN + BREAK_SPAN + ((n - hi) / upperRange) * UPPER_SPAN
                : LOWER_SPAN + BREAK_SPAN;
        }
        // 빈 구간 안 — 데이터는 없지만 눈금이 들어올 수 있다
        return LOWER_SPAN + ((n - lo) / (hi - lo)) * BREAK_SPAN;
    };

    const fromDisplay = (d) => {
        const n = Number(d);
        if (!Number.isFinite(n)) return null;
        if (n <= LOWER_SPAN) return (n / LOWER_SPAN) * lo;
        if (n >= LOWER_SPAN + BREAK_SPAN) {
            return hi + ((n - LOWER_SPAN - BREAK_SPAN) / UPPER_SPAN) * upperRange;
        }
        return lo + ((n - LOWER_SPAN) / BREAK_SPAN) * (hi - lo);
    };

    // 눈금은 아래·위 무리에서만 뽑는다. 빈 구간에는 찍지 않는다 — 접은 자리에 숫자가 있으면
    // 그 구간에 값이 있는 것처럼 보인다.
    const ticks = [
        ...ticksUpTo(0, lo, 3),
        ...ticksUpTo(hi, max, 2),
    ].map(toDisplay).filter(v => v != null);

    return {
        toDisplay,
        fromDisplay,
        ticks,
        /** 표시 좌표계에서 끊긴 자리(≈ 표시를 그릴 곳) */
        breakAt: LOWER_SPAN + BREAK_SPAN / 2,
        domain: [0, DISPLAY_MAX],
    };
}

/**
 * 값을 자릿수에 맞게 짧게 — 축 눈금·라벨 공용.
 * 작은 값이 0 으로 뭉개지지 않도록 1 미만은 소수 두 자리까지 남긴다.
 */
function formatAxisValue(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${Math.round(n / 1e8) / 10}B`;
    if (abs >= 1e6) return `${Math.round(n / 1e5) / 10}M`;
    if (abs >= 1e3) return `${Math.round(n / 100) / 10}K`;
    if (abs >= 100) return String(Math.round(n));
    if (abs >= 1) return String(Math.round(n * 10) / 10);
    if (abs === 0) return '0';
    // 1 보다 작은 값은 자릿수가 아니라 **유효숫자**로 — 소수 둘째 자리 반올림은 0.0025·0.005·0.0075 눈금을 전부 「0」 으로 찍었다(2026-10-14 신고)
    return String(Number(n.toPrecision(3)));
}

/** 값의 소수 자릿수 — 부동소수 꼬리(14.399999999999998)는 걷고 센다. 지수 표기(1e-7)는 6자리로 본다 */
function decimalsOf(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || Number.isInteger(n)) return 0;
    const str = String(Number(n.toPrecision(12)));
    if (/e/i.test(str)) return 6;
    const i = str.indexOf('.');
    return i < 0 ? 0 : Math.min(6, str.length - i - 1);
}

/**
 * 이 축에 recharts 가 그릴 눈금을 다시 구한다 — 범위가 숫자로 정해져 넘침을 허용하면 고정 범위 눈금, 아니면 「보기 좋은」 눈금.
 * (recharts 가 쓰는 규칙과 같다. tickCount 기본 5)
 */
function axisTicks({ min = 0, max, fixed = false, tickCount = 5 } = {}) {
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
    try {
        return fixed ? getTickValuesFixedDomain([min, max], tickCount, true) : getNiceTickValues([min, max], tickCount, true);
    } catch (_e) {
        return [];
    }
}

/** 한 축의 눈금들이 함께 쓸 소수 자릿수 — 가장 긴 것에 맞춘다(0.5 와 0.55 가 함께면 둘 다 두 자리) */
function axisDecimals(ticks) {
    return (ticks || []).reduce((d, v) => Math.max(d, decimalsOf(v)), 0);
}

/**
 * 눈금 글자 — 소수 자릿수를 축 단위로 맞춘다(0.50 · 0.55). 1,000 이상은 큰 수 표기(천 단위 구분 또는 K·M)를 그대로 쓴다.
 * 축의 자릿수보다 이 값의 자릿수가 많으면(눈금 추정이 빗나간 경우) 이 값 그대로 — 값을 잘라 틀리게 보이는 일은 없다.
 * @param {object} o { thousands, unit, compact } compact = 1,000 이상을 K·M 으로(formatAxisValue)
 */
function formatTick(v, decimals, o = {}) {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    const d = Math.max(decimals || 0, decimalsOf(n));
    let body;
    if (Math.abs(n) >= 1000) {
        if (o.thousands) body = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });
        else if (o.compact) body = formatAxisValue(n);
        else body = String(Number(n.toPrecision(12)));
    } else {
        body = o.thousands ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : n.toFixed(d);
    }
    return o.unit ? `${body} ${o.unit}` : body;
}

// ── 사람이 정하는 축 최대값(끌기) — 「보기 좋은」 값으로 맞춘다(2026-11-03 시험 피드백) ──────────
// 끌어서 정한 최대값이 3.71828… 같은 값이면 고정 범위 눈금이 0.92957… 이 되어 소수 자릿수가 끝없이 늘었다.
// 최대값을 1 · 1.2 · 1.5 · 2 · 2.5 · 3 · 4 · 5 · 6 · 8 × 10^k 로 맞추고, 눈금 간격도 그에 맞춘 값으로 둔다.
const NICE_MAX = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
const NICE_STEP = { 1: 0.2, 1.2: 0.3, 1.5: 0.3, 2: 0.5, 2.5: 0.5, 3: 1, 4: 1, 5: 1, 6: 2, 8: 2, 10: 2 };
const clean = (x) => Number(Number(x).toPrecision(12));

/** v 를 보기 좋은 최대값으로 — mode 'ceil' = v 이상 중 가장 작은 것, 'round' = 가장 가까운 것(로그 거리) */
function niceMax(v, mode = 'ceil') {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return n;
    const p = Math.pow(10, Math.floor(Math.log10(n)));
    const f = n / p;
    const pick = mode === 'ceil'
        ? (NICE_MAX.find(m => m >= f - 1e-9) || 10)
        : NICE_MAX.reduce((b, m) => (Math.abs(Math.log(m / f)) < Math.abs(Math.log(b / f)) ? m : b), 1);
    return clean(pick * p);
}

/** 0 부터 M 까지의 눈금 — M 이 niceMax 로 맞춘 값이면 깔끔한 간격, 아니면 고정 범위 눈금 */
function niceTicks(M) {
    const n = Number(M);
    if (!Number.isFinite(n) || n <= 0) return [];
    const p = Math.pow(10, Math.floor(Math.log10(n)));
    const f = clean(n / p);
    const step = NICE_STEP[f];
    if (!step) return axisTicks({ min: 0, max: n, fixed: true });
    const st = step * p;
    const out = [];
    for (let i = 0; i * st <= n + st * 1e-6; i++) out.push(clean(i * st));
    return out;
}

export {
    niceMax,
    niceTicks,
    decimalsOf,
    axisTicks,
    axisDecimals,
    formatTick,
    detectAxisBreak,
    makeBreakScale,
    formatAxisValue,
    niceStep,
    DISPLAY_MAX,
    LOWER_SPAN,
    BREAK_SPAN,
};
